import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useUser from "./useUser";

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before logout

export default function useSessionTimeout() {
  const { isLoggedIn, logout } = useUser();
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetTimer = useCallback(() => {
    if (!isLoggedIn) return;

    lastActivityRef.current = Date.now();

    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    // Set warning timer (25 minutes)
    warningRef.current = setTimeout(() => {
      toast.warning("Session expires in 5 minutes. Move your mouse or click to stay logged in.", {
        duration: 10000,
        id: "session-warning"
      });
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set logout timer (30 minutes)
    timeoutRef.current = setTimeout(() => {
      toast.error("Session expired due to inactivity", { duration: 5000 });
      logout();
      navigate("/login");
    }, SESSION_TIMEOUT);
  }, [isLoggedIn, logout, navigate]);

  useEffect(() => {
    if (!isLoggedIn) {
      // Clear timers when logged out
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      return;
    }

    // Activity events that reset the timer
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    
    // Throttle reset to avoid excessive calls
    let throttleTimer = null;
    const throttledReset = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          resetTimer();
          throttleTimer = null;
        }, 1000);
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Initial timer setup
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [isLoggedIn, resetTimer]);

  // Return last activity time (useful for debugging)
  return {
    lastActivity: lastActivityRef.current,
    resetTimer
  };
}
